import { EventEmitter, Component, Fragment, h, Prop, Event, forceUpdate } from '@stencil/core';
import { PersonDoc, RfiDoc, RFISettingsDoc, WorkflowState } from 'common';
import { PeopleService } from 'src/global/service/PeopleService';
import { RfiWorkflowUtils } from '..';
import { PersonInfoModal } from '../../components/concourse-person-selector/person-info-modal';

export interface RfiPopulated extends RfiDoc {
  number: string;
  assignee: string;
  state: WorkflowState;
}

@Component({
  tag: 'view-table',
  styleUrl: 'concourse-rfis.css',
  //shadow: true,
})
export class ViewTable {
  @Prop() canWrite: boolean;
  @Prop({ mutable: true }) rfis: RfiDoc[] = [];
  @Prop() people: PersonDoc[] = [];
  @Prop() settings: RFISettingsDoc;

  @Event() rfisEdited: EventEmitter<RfiDoc[]>;
  @Event() rfiSelected: EventEmitter<RfiDoc>;

  private dragging: HTMLElement;
  private over: HTMLElement;

  get rfisPopulated(): RfiPopulated[] {
    return this.rfis.map(rfi => {
      const assignee: PersonDoc = rfi.assignees && rfi.assignees.length ? PeopleService.findPerson(this.people, rfi.assignees[0]) : null;
      const state = RfiWorkflowUtils.getCurrentState(rfi);
      return { ...rfi, number: rfi.code ?? '', assignee: PeopleService.getFullName(assignee), state };
    }); //.filter(rfi => rfi.stateId == "Answer");
  }

  componentDidLoad() {
    const tableColumns = document.getElementsByTagName('th');
    for (let i = 0; i < tableColumns.length; i++) {
      const col = tableColumns.item(i);
      col.addEventListener('click', () => {
        this.onColumnSelected(col.id);
      });
    }
  }

  onColumnSelected(column: string) {
    const parts = document.location.hash.split('/');
    const sortColumn = parts[parts.indexOf('table') + 1] || 'state';
    const sortDesc = sortColumn.charAt(0) == '-';

    // if the same columns was clicked on twice, toggle direction
    const desc = sortColumn.includes(column) ? !sortDesc : false;
    const clean = parts.slice(0, parts.indexOf('table') + 1);
    document.location.hash = clean
      .join('/')
      .concat(desc ? '/-' : '/')
      .concat(column);
    forceUpdate(this);
  }

  onRowDragStart(e) {
    this.dragging = e.target;
  }

  onDragOver(e: Event) {
    e.preventDefault();
    this.over = (e.target as HTMLElement).parentElement;

    const rows = Array.from(document.querySelectorAll('.rfi-row'));
    rows.forEach(row => {
      if (row.id == this.over.id && (row as HTMLElement).dataset.state == this.dragging.dataset.state) row.setAttribute('style', 'border-top: 2px solid white;');
      else row.setAttribute('style', 'border-top: inherit');
    });
  }

  onDragEnd(e: Event) {
    e.preventDefault();

    this.over.parentElement.insertBefore(this.dragging, this.over);

    // Get all the RFI rows for the same state as the dragging rfi
    const rows = Array.from(document.querySelectorAll('.rfi-row')).filter(row => (row as HTMLElement).dataset.state == this.dragging.dataset.state);

    const rfis: RfiDoc[] = [];
    rows.forEach((row, index) => {
      row.setAttribute('style', 'border-top: inherit');
      const rfi: RfiDoc = this.rfis.find(rfi => rfi._id == row.id);
      rfi.pos = index + 1;
      rfis.push(rfi);
    });

    // Super nasty hack to clear the render method.
    // Without this, the rows seem to get re-used with the old data.
    this.rfis = [];
    forceUpdate(this);

    // This will update the data and allow for a fresh list of 'rfis' to be passed
    this.rfisEdited.emit(rfis);
  }

  doSort(sortColumn: string, sortDesc: boolean): RfiPopulated[] {
    console.log('doSort', sortColumn, sortDesc);
    const rfis = this.rfisPopulated;
    rfis.sort(this.sortProperty(sortColumn));
    return rfis;
  }

  sortProperty(property) {
    var sortOrder = 1;
    if (property[0] === '-') {
      sortOrder = -1;
      property = property.substr(1);
    }
    return function (a: RfiPopulated, b: RfiPopulated) {
      var sortPos = a.pos < b.pos ? -1 : a.pos > b.pos ? 1 : 0;
      if (a[property] == b[property]) return sortPos * sortOrder;

      var sortProp = a[property] < b[property] ? -1 : a[property] > b[property] ? 1 : 0;
      return sortProp * sortOrder;
    };
  }

  handlePersonClick(event, person: PersonDoc) {
    event.stopPropagation();
    event.preventDefault();
    PersonInfoModal.presentModal(person);
  }

  render() {
    const parts = document.location.hash.split('/');
    const sortColumn = parts[parts.indexOf('table') + 1] || 'state';
    const sortDesc = sortColumn.charAt(0) == '-';
    const rfis = this.doSort(sortColumn, sortDesc);

    const getClassname = (column): string => {
      if (!sortColumn.includes(column)) return '';
      return sortColumn.charAt(0) == '-' ? 'sort-asc' : 'sort-desc';
    };
    return (
      <Fragment>
        <table>
          <tr>
            <th id="state" class={getClassname('state')}>
              State
            </th>
            <th id="pos" class={getClassname('pos')}>
              Pos
            </th>
            <th id="number" class={getClassname('number')}>
              #Num
            </th>
            <th id="assignee" class={getClassname('assignee')}>
              Assignees
            </th>
            <th id="createdDate" class={getClassname('createdDate')} style={{ 'min-width': '110px' }}>
              Created
            </th>
            <th id="dueDate" class={getClassname('dueDate')} style={{ 'min-width': '110px' }}>
              Due
            </th>
            <th id="subject" class={getClassname('subject')}>
              Subject
            </th>
            <th>&nbsp;</th>
          </tr>
          {rfis.map((rfi, index) => {
            const state = RfiWorkflowUtils.getCurrentState(rfi);
            const reorderable: boolean = this.canWrite && sortColumn == 'state' && !!state;
            return (
              <tr
                id={rfi._id}
                data-state={state?.id || 'draft'}
                class="rfi-row"
                draggable={reorderable}
                onDragStart={e => this.onRowDragStart(e)}
                onDragOver={e => this.onDragOver(e)}
                onDragEnd={e => this.onDragEnd(e)}
                onClick={() => this.rfiSelected.emit(rfi)}
              >
                <td>{state ? state.action : ' '}</td>
                <td>{!!state ? rfi.pos : ' '}</td>
                <td>#{rfi.code}</td>
                <td>
                  {state?.dueBy.map(id => {
                    const person = this.people.find(p => p._id == id);
                    return person ? (
                      <a href="javascript:void(0)" onClick={e => this.handlePersonClick(e, person)}>
                        {PeopleService.getFullName(person)}
                      </a>
                    ) : (
                      <a href={'mailto:' + id}>{id}</a>
                    );
                  })}
                </td>
                <td>{rfi.created_date ? rfi.created_date.substring(0, 10) : ' '}</td>
                <td>{rfi.due_date?.substring(0, 10)}</td>
                <td>{rfi.subject}</td>
                <td>{reorderable ? <ion-icon name="reorder-two-outline" width="16" height="16"></ion-icon> : ' '}</td>
              </tr>
            );
          })}
        </table>
      </Fragment>
    );
  }
}
